// src/pages/DevRegisterPage.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUser, FiMail, FiLock, FiShield } from 'react-icons/fi';
import { useNotification } from '../context/NotificationContext';
import { AuthService } from '../services/auth.service';

const MFA_PENDING_KEY = 'mfa_pending';

export default function DevRegisterPage() {
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const canSubmit = !!name.trim() && !!email.trim() && !!password.trim() && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !password.trim()) {
      addNotification('Completa todos los campos.', 'error');
      return;
    }

    setLoading(true);

    try {
      const res = await AuthService.registerAdmin({ name: name.trim(), email: email.trim(), password });

      if ((res as any)?.mfaSetupRequired === true) {
        const setupToken = (res as any).setupToken as string;
        localStorage.setItem(MFA_PENDING_KEY, JSON.stringify({ type: 'register', setupToken, email: email.trim() }));
        addNotification('Te enviamos un código a tu correo.', 'info');
        navigate('/mfa/setup', { replace: true });
        return;
      }

      addNotification('Administrador creado exitosamente. Inicia sesión.', 'success');
      navigate('/login', { replace: true });
    } catch (error: any) {
      const message = error?.payload?.message || error?.message || 'No se pudo completar el registro.';
      addNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
        background: 'radial-gradient(1000px 600px at 20% 10%, rgba(103,58,183,0.18), transparent 60%), radial-gradient(900px 600px at 90% 30%, rgba(33,150,243,0.14), transparent 55%), #f4f6f8'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          background: 'linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%)',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 28,
          padding: 18,
          boxShadow: '0 26px 52px rgba(0, 0, 0, 0.10), 0 4px 10px rgba(0, 0, 0, 0.06)'
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '8px 8px 12px' }}>
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: 18,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(103,58,183,0.12)',
              color: '#3f2a93'
            }}
          >
            <FiShield size={22} />
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 950, color: '#0f172a', fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
              Registro de administrador
            </div>
            <div style={{ color: '#64748b', fontWeight: 750, lineHeight: '1.2rem' }}>
              Acceso por URL directa para alta de administradores.
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14, padding: 8 }}>
          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ color: '#64748b', fontWeight: 900, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Nombre
            </span>
            <div style={{ position: 'relative' }}>
              <FiUser style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#64748b', opacity: 0.9 }} />
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Tu nombre completo"
                autoComplete="name"
                style={{
                  width: '100%',
                  height: 46,
                  padding: '12px 14px 12px 44px',
                  borderRadius: 16,
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: '#fff',
                  fontWeight: 850,
                  color: '#0f172a',
                  outline: 'none'
                }}
              />
            </div>
          </label>

          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ color: '#64748b', fontWeight: 900, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Correo
            </span>
            <div style={{ position: 'relative' }}>
              <FiMail style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#64748b', opacity: 0.9 }} />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@profunding.com"
                autoComplete="email"
                style={{
                  width: '100%',
                  height: 46,
                  padding: '12px 14px 12px 44px',
                  borderRadius: 16,
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: '#fff',
                  fontWeight: 850,
                  color: '#0f172a',
                  outline: 'none'
                }}
              />
            </div>
          </label>

          <label style={{ display: 'grid', gap: 8 }}>
            <span style={{ color: '#64748b', fontWeight: 900, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Contraseña
            </span>
            <div style={{ position: 'relative' }}>
              <FiLock style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#64748b', opacity: 0.9 }} />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Mínimo 5 caracteres"
                autoComplete="new-password"
                style={{
                  width: '100%',
                  height: 46,
                  padding: '12px 14px 12px 44px',
                  borderRadius: 16,
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: '#fff',
                  fontWeight: 850,
                  color: '#0f172a',
                  outline: 'none'
                }}
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              height: 46,
              borderRadius: 999,
              border: 'none',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 950,
              color: '#fff',
              background: 'linear-gradient(135deg, var(--color-primario), var(--color-primario-oscuro))',
              opacity: canSubmit ? 1 : 0.7
            }}
          >
            {loading ? 'Creando...' : 'Crear administrador'}
          </button>

          <div style={{ textAlign: 'center', color: '#64748b', fontWeight: 750, fontSize: '0.92rem' }}>
            Esta pantalla no está ligada al menú. Solo es accesible escribiendo la URL completa.
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
            <Link
              to="/login"
              style={{
                textDecoration: 'none',
                color: 'var(--color-primario)',
                fontWeight: 900,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <FiArrowLeft /> Volver a inicio de sesión
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
